<template>
  <div class="w-full h-full flex">
    <div v-if="shopFeatureAvailable" class="w-full h-full flex flex-col">
      <ConfirmDialog />
      <tabs-wrapper v-if="shopExist" contentClass="md:px-4" @tab-select="onTabSelect">
        <template #header-end-0>
          <Button
            label="הוספת מוצר"
            icon="pi pi-plus"
            size="small"
            @click="addShopItem" />
        </template>
        <template #header-end-1>
          <Button
            label="מכירה ידנית"
            icon="pi pi-plus"
            size="small"
            @click="openManualSale" />
        </template>

        <!-- ── Products tab ── -->
        <tab title="מוצרים">
          <div class="flex flex-col h-full pt-2 overflow-y-hidden md:overflow-y-auto w-full">
            <div class="flex flex-col flex-1 min-h-0 overflow-y-auto px-4 pt-2 md:pt-4">
              <div class="w-full flex items-center gap-3 mb-4">
                <IconField class="w-full md:max-w-[300px]">
                  <InputIcon class="pi pi-search" />
                  <InputText size="small" v-model="searchValue" placeholder="חיפוש" class="w-full" />
                </IconField>
              </div>
              <div v-if="products"
                   class="w-full flex flex-col pb-4 md:flex-row md:flex-wrap md:content-start overflow-x-hidden gap-3 md:gap-4">
                <shop-item
                  v-for="product of products"
                  :key="product.id"
                  :product="product"
                  @on-click="editShopItem(product)"
                />
              </div>
            </div>
          </div>
        </tab>

        <!-- ── Orders tab ── -->
        <tab title="הזמנות" :badge="openOrdersCount">
          <product-orders ref="productOrdersRef" @open-count-changed="openOrdersCount = $event" />
        </tab>

        <!-- ── Settings tab ── -->
        <tab title="הגדרות">
          <div class="w-full px-4 pt-2">
            <shop-settings />
          </div>
        </tab>
      </tabs-wrapper>

      <div v-else class="w-full h-max flex flex-col justify-center items-center my-auto">
        <img
          src="../../../assets/img/admin/create-store.svg"
          class="w-44 mb-4"
          alt="יצירת חנות">
        <span class="text-lg font-medium">נראה שאתם עדיין לא מוכרים מוצרים</span>
        <span class="text-center px-4 font-light">הוסיפו את המוצר הראשון שלכם עכשיו, זה לוקח פחות מ-5 דקות, ותנו ללקוחות שלכם להוסיף מוצרים לתור הבא שלהם!</span>
        <div
          class="relative py-2 px-4 border border-solid m-2 bg-primary-500 text-white rounded-3xl border-purple-800 flex gap-2 justify-center items-center mt-4"
          @click="addShopItem">
          <i class="pi pi-user-plus" />
          <span class="text-sm">הוספת מוצר ראשון</span>
        </div>
      </div>
    </div>

    <div v-else class="w-full h-full flex-col flex justify-center items-center px-4 relative">
      <feature-is-not-available />
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAdminStore } from '@app/stores';
import { checkFeatureIsAvailable } from '@torim-app/utils';
import { AdminRouteNames } from '@app/router/route-names';
import { ClientFeatures, IShopItem } from '@torim-app/types';
import ShopAddProduct from '@app/components/admin/pages/shop-management/ShopAddProduct.vue';
import ShopItem from '@app/components/admin/pages/shop-management/ShopItem.vue';
import ShopSettings from '@app/components/admin/pages/shop-management/ShopSettings.vue';
import ProductOrders from '@app/components/admin/pages/shop-management/ProductOrders.vue';
import TabsWrapper from '@app/components/admin/components/admin-tabs/TabsWrapper.vue';
import Tab from '@app/components/admin/components/admin-tabs/Tab.vue';
import { useDrawer } from '@torim-app/shared-components';
import InputIcon from 'primevue/inputicon';
import IconField from 'primevue/iconfield';
import { useHeaderAction } from '@app/composables/header-action';
import ConfirmDialog from 'primevue/confirmdialog';
import { trackEvent } from '@app/composables/posthog';
import { ProductOrdersApi } from '@app/api/admin/product-orders-api';

const adminStore = useAdminStore();
const route = useRoute();
const { closeDrawer, openDrawer } = useDrawer();
const { setHeaderAction, clearHeaderAction } = useHeaderAction();

trackEvent('shop_management_viewed');
adminStore.setPage(AdminRouteNames.Shop);

const searchValue = ref<string>('');
const shopFeatureAvailable = ref(checkFeatureIsAvailable(ClientFeatures.Shop, adminStore.businessSiteData.plan));
const openOrdersCount = ref(0);
const productOrdersRef = ref<{ openManualSale: () => void } | null>(null);
const activeTabIndex = ref<number>(route.query.tab !== undefined ? Number(route.query.tab) : 0);

// The mobile header "+" replaces the old bottom button, and is tab-aware:
// Products → add product, Orders → manual sale, Settings → nothing.
const applyHeaderActionForTab = (idx: number) => {
  if (!shopFeatureAvailable.value) return clearHeaderAction();
  if (idx === 1) setHeaderAction({ icon: 'pi-plus', onClick: openManualSale, ariaLabel: 'מכירה ידנית' });
  else if (idx === 2) clearHeaderAction();
  else setHeaderAction({ icon: 'pi-plus', onClick: addShopItem, ariaLabel: 'הוספת מוצר' });
};

// Child TabsWrapper emits the initial tab (from ?tab=) on its own mount, which runs
// before this parent's onMounted — so read activeTabIndex rather than hardcoding 0.
onMounted(() => applyHeaderActionForTab(activeTabIndex.value));

const shopExist = computed(() => !!adminStore.businessSiteData.shop);

const onTabSelect = (idx: number) => {
  activeTabIndex.value = idx;
  applyHeaderActionForTab(idx);
};

const openManualSale = () => {
  productOrdersRef.value?.openManualSale();
};

const addShopItem = () => {
  trackEvent('shop_add_product_clicked');
  openDrawer(ShopAddProduct, {
    mode: 'new',
    onClose: closeDrawer
  }, {
    title: 'הוספת מוצר'
  });
};

const editShopItem = (shopItem: IShopItem) => {
  trackEvent('shop_edit_product_clicked');
  openDrawer(ShopAddProduct, {
    shopItem,
    mode: 'edit',
    onClose: closeDrawer
  }, {
    title: 'עריכת מוצר'
  });
};

const products = computed(() => {
  let items = adminStore.businessSiteData?.shop?.items ?? [];
  if (searchValue.value)
    items = items.filter(x => x.name.toLowerCase().includes(searchValue.value.toLowerCase()));
  return items.filter(x => x.active);
});

onMounted(async () => {
  if (!shopExist.value || !shopFeatureAvailable.value) return;
  try {
    const { count } = await ProductOrdersApi.getOpenOrdersCount();
    openOrdersCount.value = count;
  } catch (e) {
    console.error(e);
  }
});
</script>

<style scoped lang="scss">

</style>
